/**
 * TPO / Market Profile chart STYLE.
 *
 * Registered as an internal klinecharts indicator (the VOL_OVERLAY precedent)
 * rather than a user-addable one: TPO is not something you layer onto a candle
 * chart, it is a chart type you switch to. EdgeChart creates and removes it in
 * step with the `tpo` chart type, and it never appears in the indicator picker.
 *
 * Because the candles are replaced by a thin close-price line when this style is
 * active, each session's profile owns its FULL column span — there is nothing to
 * overlap and nothing to clamp against.
 */

import { registerIndicator, type Indicator, type KLineData } from 'klinecharts'
import { numParam } from './calcParams'
import { type IndicatorTemplateParam, registerOnce } from './registrationUtils'
import {
  autoTickSize,
  bracketSchemeFor,
  buildTpoProfile,
  createRthBracketOf,
  rowSizeFromSessionRanges,
  type TpoProfile,
} from './tpoProfile'
import { normalizeTpoSettings, type TpoSettings } from './tpoSettings'
import { isEarlyCloseDay } from './marketCalendar'
import { isRegularSession } from './sessionFilter'
import {
  paintTpoLevels,
  paintTpoProfile,
  paintTpoVolumeBand,
  type TpoChromeCtx,
  type TpoPaintParams,
} from './tpoRenderer'
import { binVolumeOnGrid, collectProfileBars, type VolumeProfile } from './volumeProfile'
import {
  DEFAULT_SESSION_TZ,
  sessionDayKey,
  sessionTimezoneOf,
  timestampsOf,
  toSessionAnchor,
  visibleSessionRuns,
  type SessionAnchor,
  type SessionRun,
} from './sessionDay'

const TPO_INDICATOR_NAME = 'TPO'

/** Classic Market Profile half-hour bracket. */
const DEFAULT_BRACKET_MINUTES = 30
const DEFAULT_VALUE_AREA_PCT = 70
/** Calendar day by default, matching the session volume profile's default. */
const DEFAULT_SESSION_ANCHOR: SessionAnchor = 'calendar-day'

export interface TpoDrawParams {
  ctx: TpoChromeCtx
  kLineDataList: KLineData[]
  visibleRange: { from: number; to: number }
  bounding: { width: number; height: number }
  yAxis: { convertToPixel: (value: number) => number }
  xAxis: { convertToPixel: (dataIndex: number) => number }
  barSpace: { halfBar: number }
  indicator: {
    calcParams?: unknown[]
    extendData?: unknown
  }
}

// ─── Draw-plan memo ──────────────────────────────────────────────────────────
//
// Everything below `computeTpoDrawPlan` is deliberately viewport-independent —
// the row size comes from full-day session ranges and each profile covers its
// whole trading day, so none of it changes during a pan or zoom. Recomputing it
// per frame (a full-dataset walk plus a rebuild of every visible profile) was
// the dominant frame cost. The memo keys on what CAN change it: the data's
// shape, the live bar's values, and the settings that feed the binning.
//
// Keyed by the bar array itself (klinecharts hands `draw` its internal
// dataList, stable across frames), so multiple charts each get their own entry
// and a discarded chart's memo is garbage-collected with its data.

interface TpoSessionEntry {
  /** Fingerprint of the session's bars (plus live-bar values when it is the
   *  newest session), so an in-place tick rebuilds only this session. */
  key: string
  profile: TpoProfile | null
  volume: VolumeProfile | null
  /** Volume is computed lazily — only once the band is actually shown. */
  hasVolume: boolean
}

interface TpoDrawMemo {
  tickKey: string
  /** Memoized session-ranges row size; 0 = nothing usable, caller falls back. */
  tickSizeBase: number
  sessionsKey: string
  sessions: Map<string, TpoSessionEntry>
}

/** Sessions kept per chart before the map resets. Panning is a forward/backward
 *  sweep, so a cleared map simply refills with the sessions in view. */
const SESSION_CACHE_LIMIT = 256

const drawMemo = new WeakMap<KLineData[], TpoDrawMemo>()

function memoFor(list: KLineData[]): TpoDrawMemo {
  let memo = drawMemo.get(list)
  if (!memo) {
    memo = { tickKey: '', tickSizeBase: 0, sessionsKey: '', sessions: new Map() }
    drawMemo.set(list, memo)
  }
  return memo
}

/** The newest bar's mutable fields — the only data that changes without the
 *  array's length or timestamps changing (an in-place live tick). */
function liveBarKey(list: KLineData[]): string {
  const last = list[list.length - 1]
  if (!last) return 'none'
  return `${last.timestamp}|${last.high}|${last.low}|${last.close}|${last.volume ?? 0}`
}

/**
 * Row size from the instrument's recent SESSION RANGES — a volatility anchor,
 * never the viewport, so the binning (and therefore the POC/VAH/VAL prices)
 * cannot move when you zoom or scroll. Sizing off volatility rather than price
 * magnitude is what keeps it useful: two $3 stocks, one moving 2% a day and one
 * 50%, need very different rows.
 *
 * This is the full-dataset walk the memo exists for; a miss costs one pass.
 */
function sessionRangeTick(
  kLineDataList: KLineData[],
  timestamps: number[],
  tz: string,
  anchor: SessionAnchor,
  inScope: (timestamp: number) => boolean,
): number {
  const sessionRanges: number[] = []
  let i = 0
  while (i < kLineDataList.length) {
    const key = sessionDayKey(timestamps[i], tz, anchor)
    let hi = -Infinity
    let lo = Infinity
    let j = i
    while (j < kLineDataList.length && sessionDayKey(timestamps[j], tz, anchor) === key) {
      const d = kLineDataList[j]
      if (d && inScope(d.timestamp)) {
        if (d.high > hi) hi = d.high
        if (d.low < lo) lo = d.low
      }
      j++
    }
    if (hi > lo) sessionRanges.push(hi - lo)
    i = j
  }
  return rowSizeFromSessionRanges(sessionRanges)
}

export interface TpoSessionSpan {
  run: SessionRun
  profile: TpoProfile
  /** Companion volume profile on the TPO's grid; null when the band is off or
   *  the session has no usable volume. */
  volume: VolumeProfile | null
}

export interface TpoDrawPlan {
  tickSize: number
  spans: TpoSessionSpan[]
}

export interface TpoPlanOptions {
  tz: string
  anchor: SessionAnchor
  bracketMinutes: number
  valueAreaPct: number
  settings: TpoSettings
}

/**
 * The viewport-independent half of the TPO draw: one profile per visible
 * trading session, plus the row size they are binned at — memoized per bar
 * array so a pan/zoom frame reuses the previous frame's profiles untouched.
 *
 * Cache invalidation, from broadest to narrowest:
 *  - a settings/tick change flips `sessionsKey` and rebuilds every session;
 *  - an append or prepend changes a session's bar-count/timestamp fingerprint;
 *  - an in-place live tick changes only the newest session's fingerprint.
 * Returns referentially-equal profiles on a warm hit, which is also what the
 * unit tests assert the caching by.
 */
export function computeTpoDrawPlan(
  kLineDataList: KLineData[],
  visibleRange: { from: number; to: number },
  opts: TpoPlanOptions,
): TpoDrawPlan | null {
  const { tz, anchor, bracketMinutes, valueAreaPct, settings } = opts
  const from = Math.max(0, visibleRange.from)
  const to = Math.min(kLineDataList.length, visibleRange.to)
  if (to <= from) return null

  /**
   * Regular-hours-only profiles read from a filtered view of the bars.
   *
   * Applied to the row-size derivation as well as the binning, so a pre-market
   * spike cannot set the row width for a session whose profile excludes it.
   * Session RUNS are still computed over every bar, because a run defines the
   * day's column span on screen — the profile just declines to count some of it.
   */
  const inScope = (timestamp: number): boolean =>
    settings.profileSession === 'regular' ? isRegularSession(timestamp, tz) : true

  const timestamps = timestampsOf(kLineDataList)
  // Each visible session grown to its FULL trading day. A profile is a property
  // of the session, not of the viewport — so it does not change as you scroll.
  const runs = visibleSessionRuns(timestamps, from, to, tz, anchor)
  if (runs.length === 0) return null

  const memo = memoFor(kLineDataList)

  const len = kLineDataList.length
  const tickKey = `${tz}|${anchor}|${settings.profileSession}|${len}|${timestamps[0]}|${liveBarKey(kLineDataList)}`
  if (memo.tickKey !== tickKey) {
    memo.tickKey = tickKey
    memo.tickSizeBase = sessionRangeTick(kLineDataList, timestamps, tz, anchor, inScope)
  }

  // An explicit width wins outright. Guarded on > 0 so the default of 0 means
  // "inherit auto", making the mode flip a no-op until a width is typed.
  const manual = settings.rowSizeMode === 'manual' ? settings.manualRowSize : 0
  let tickSize = Number.isFinite(manual) && manual > 0 ? manual : memo.tickSizeBase
  if (!(tickSize > 0)) {
    // Single thin session, or nothing usable — fall back to the visible span so
    // the profile still renders rather than vanishing. Viewport-dependent by
    // construction, so it stays outside the memo.
    let fMin = Infinity
    let fMax = -Infinity
    for (let i = from; i < to; i++) {
      const d = kLineDataList[i]
      if (!d) continue
      if (d.low < fMin) fMin = d.low
      if (d.high > fMax) fMax = d.high
    }
    if (!Number.isFinite(fMin) || !Number.isFinite(fMax)) return null
    tickSize = autoTickSize(fMin, fMax)
  }

  const band = settings.volumeProfile
  const wantVolume = band.visible && Math.min(Math.max(band.widthPct, 0), 90) > 0

  const sessionsKey = `${tickSize}|${bracketMinutes}|${valueAreaPct}|${settings.profileSession}|${anchor}|${tz}`
  if (memo.sessionsKey !== sessionsKey) {
    memo.sessionsKey = sessionsKey
    memo.sessions.clear()
  }

  const lastIdx = len - 1
  const spans: TpoSessionSpan[] = []
  for (const run of runs) {
    const sessionDate = sessionDayKey(timestamps[run.startIdx], tz, anchor)
    const barCount = run.endIdx - run.startIdx + 1
    let entryKey = `${timestamps[run.startIdx]}|${timestamps[run.endIdx]}|${barCount}`
    if (run.endIdx === lastIdx) entryKey += `|${liveBarKey(kLineDataList)}`

    let entry = memo.sessions.get(sessionDate)
    if (!entry || entry.key !== entryKey) {
      const bars = collectProfileBars(kLineDataList, run.startIdx, run.endIdx + 1, inScope)
      let profile: TpoProfile | null = null
      if (bars.length > 0) {
        /**
         * Brackets count from the 09:30 CLOCK, not from the run's first bar —
         * see `createRthBracketOf` for why the letter grid is anchored there.
         * `sessionDayKey` returns the run's regular-session date under either
         * anchor mode, which is exactly the reference the indexer needs.
         */
        profile = buildTpoProfile(bars, {
          tickSize,
          bracketMinutes,
          valueAreaPct,
          bracketOf: createRthBracketOf(sessionDate, bracketMinutes, tz),
          // Early close shortens the uppercase run, so 13:00 goes lowercase on
          // those ~3 days a year instead of claiming a regular-session letter.
          bracketScheme: bracketSchemeFor(bracketMinutes, isEarlyCloseDay(sessionDate)),
        })
      }
      entry = { key: entryKey, profile, volume: null, hasVolume: false }
      if (memo.sessions.size >= SESSION_CACHE_LIMIT) memo.sessions.clear()
      memo.sessions.set(sessionDate, entry)
    }

    if (entry.profile && wantVolume && !entry.hasVolume) {
      /**
       * Binned onto the TPO's OWN grid, so bucket i is row i and the two
       * ladders line up exactly. Binning independently would divide the same
       * range into its own equal buckets and land up to a tick off the
       * grid-snapped rows. Lazy: computed the first time the band is shown.
       */
      const bars = collectProfileBars(kLineDataList, run.startIdx, run.endIdx + 1, inScope)
      entry.volume = binVolumeOnGrid(bars, {
        origin: entry.profile.gridOrigin,
        tickSize: entry.profile.tickSize,
        rowCount: entry.profile.rows.length,
        valueAreaPct,
      })
      entry.hasVolume = true
    }

    if (entry.profile) {
      spans.push({ run, profile: entry.profile, volume: wantVolume ? entry.volume : null })
    }
  }

  return { tickSize, spans }
}

/**
 * Paint one TPO profile per visible trading session.
 *
 * The profile spans high-to-low because a TPO asks "did price TRADE at this level
 * during this bracket", which is the wick range. The overlaid line draws closes
 * only, so the profile reaching past it is correct, not a defect — the axis is
 * what has to accommodate it (see the `figures` note in the registration).
 */
export function drawTpo(params: TpoDrawParams): boolean {
  const { ctx, kLineDataList, visibleRange, yAxis, xAxis, barSpace, indicator } = params
  const cp = indicator.calcParams ?? []
  const bracketRaw = numParam(cp, 0, DEFAULT_BRACKET_MINUTES)
  const bracketRequested = bracketRaw > 0 ? Math.floor(bracketRaw) : DEFAULT_BRACKET_MINUTES
  const valueAreaPct = numParam(cp, 1, DEFAULT_VALUE_AREA_PCT)
  const anchor = toSessionAnchor(cp[2])
  const ext = indicator.extendData as
    | { timezone?: string; tpo?: Partial<TpoSettings>; periodMs?: number }
    | undefined
  /**
   * The EXCHANGE timezone, not the chart's display timezone.
   *
   * Nothing passes it today, so this is always the ET default — which is what it
   * must be. It decides three exchange-local facts: which calendar day a bar
   * belongs to, whether a bar is regular-session, and where bracket `A` starts.
   * Feeding a user's display preference in here would move the 09:30 anchor to
   * 09:30 in THEIR zone and quietly relabel every profile.
   */
  const tz = sessionTimezoneOf(ext)

  /**
   * A bracket can never be finer than one bar.
   *
   * `bracketIndexOf` maps each bar to exactly one bracket, so asking for 30-minute
   * brackets on an hourly chart assigns bars to brackets 0, 2, 4… — every other
   * letter silently vanishes and the profile misstates the session. Clamping to
   * the bar interval degrades to "one bracket per bar", which is the finest
   * honest answer the data supports.
   *
   * Note this does NOT limit the chart's timeframe. Finer bars make a TPO more
   * faithful, not less: the union of six 5-minute ranges excludes levels price
   * gapped over inside the half hour, which a single 30-minute high/low cannot.
   */
  const barMinutes =
    ext?.periodMs && Number.isFinite(ext.periodMs) ? Math.ceil(ext.periodMs / 60_000) : 0
  const bracketMinutes = Math.max(bracketRequested, barMinutes || 1)
  // Filled out from the defaults so a partial payload (or none at all) still
  // yields a complete settings object for the renderer — nested objects included,
  // which a shallow spread would have left undefined.
  const settings: TpoSettings = normalizeTpoSettings(ext?.tpo)

  const plan = computeTpoDrawPlan(kLineDataList, visibleRange, {
    tz,
    anchor,
    bracketMinutes,
    valueAreaPct,
    settings,
  })
  if (!plan) return true

  const painted: TpoPaintParams[] = []
  for (const { run, profile, volume } of plan.spans) {
    const leftX = xAxis.convertToPixel(run.startIdx) - barSpace.halfBar
    const rightX = xAxis.convertToPixel(run.endIdx) + barSpace.halfBar

    /**
     * The volume band takes a share of the RIGHT of the session and the TPO keeps
     * the rest; with the band off, the TPO reclaims the whole span.
     *
     * `widthPct` is clamped below 100 so the TPO — the chart type the user
     * actually selected — can never be squeezed to nothing.
     */
    const band = settings.volumeProfile
    const bandPct = band.visible ? Math.min(Math.max(band.widthPct, 0), 90) : 0
    const bandWidth = ((rightX - leftX) * bandPct) / 100
    const tpoRightX = rightX - bandWidth

    paintTpoProfile(ctx, yAxis, { profile, leftX, rightX: tpoRightX, settings })

    if (volume && bandWidth > 0) {
      paintTpoVolumeBand(ctx, yAxis, {
        profile: volume,
        rows: profile.rows,
        leftX: tpoRightX,
        rightX,
        settings,
      })
    }

    // Levels span the FULL session, band included: a POC is a price statement
    // about the whole day, so stopping it at the TPO's edge would read as though
    // it applied only to the time profile.
    painted.push({ profile, leftX, rightX, settings })
  }

  // Levels go on top of EVERY session's blocks — painted inline, the next
  // session's blocks would cover the previous session's guides and labels.
  for (const span of painted) paintTpoLevels(ctx, yAxis, span)

  return true
}

/**
 * STACKED-mode axis feed — each bar carries its whole SESSION's high and low,
 * not its own.
 *
 * That distinction is the point for stacked mode. The histogram collapses every
 * row to the session's left edge, so the whole day's profile is on screen at any
 * scroll position inside the session — and klinecharts' `calcRange` only folds
 * in VISIBLE bars, so per-bar extents would let the axis clip rows whose prices
 * only traded off-screen. Repeating the session extent across its bars means any
 * single visible bar pulls the axis wide enough for the whole profile.
 *
 * Expanded mode wants the opposite — see `tpoBarAxisValues`.
 *
 * One shared object per session run (the drawPivotSegments trick), so downstream
 * run detection can compare by reference.
 *
 * `inScope` mirrors the profile's `Profile session` scope. Out-of-scope bars
 * contribute only their CLOSE: the profile excludes their wicks, so folding a
 * pre-market spike's high into the axis would leave dead space above a profile
 * that deliberately ends short of it — but the close line still draws across
 * the whole day, so closes must stay in range or the line clips instead.
 */
export function tpoAxisValues(
  dataList: KLineData[],
  tz: string = DEFAULT_SESSION_TZ,
  anchor: SessionAnchor = 'calendar-day',
  inScope?: (timestamp: number) => boolean,
): Array<{ h: number; l: number }> {
  const out: Array<{ h: number; l: number }> = new Array(dataList.length)
  let i = 0
  while (i < dataList.length) {
    const key = sessionDayKey(dataList[i].timestamp, tz, anchor)
    let j = i
    let hi = -Infinity
    let lo = Infinity
    while (j < dataList.length && sessionDayKey(dataList[j].timestamp, tz, anchor) === key) {
      const d = dataList[j]
      if (!inScope || inScope(d.timestamp)) {
        if (d.high > hi) hi = d.high
        if (d.low < lo) lo = d.low
      } else {
        if (d.close > hi) hi = d.close
        if (d.close < lo) lo = d.close
      }
      j++
    }
    const shared = { h: hi, l: lo }
    for (let k = i; k < j; k++) out[k] = shared
    i = j
  }
  return out
}

/**
 * EXPANDED-mode axis feed — each bar carries its OWN extent.
 *
 * In expanded mode every bracket owns its own time column, so off-screen time is
 * off-screen profile: zooming past after-hours removes its columns from view,
 * and a wild after-hours range should stop dictating the scale of the regular
 * session you are actually looking at. Per-bar extents give exactly that —
 * `calcRange` folds in visible bars only, so the axis follows the visible slice
 * of the profile. (Columns are uniform across the session's span while bars are
 * uniform in index space, so the two line up to within a column at the screen
 * edges — the same tolerance every candle chart accepts at its edges.)
 *
 * Out-of-scope bars contribute their close only, same contract as the stacked
 * feed: the profile excludes their wicks, the close line still needs them.
 */
export function tpoBarAxisValues(
  dataList: KLineData[],
  inScope?: (timestamp: number) => boolean,
): Array<{ h: number; l: number }> {
  return dataList.map((d) =>
    !inScope || inScope(d.timestamp) ? { h: d.high, l: d.low } : { h: d.close, l: d.close },
  )
}

/** Register the internal `TPO` chart-style indicator with klinecharts (once). */
export const registerTpoIndicator = registerOnce(() => {
  registerIndicator({
    name: TPO_INDICATOR_NAME,
    shortName: 'TPO',
    // 'price', not 'volume' — same precision trap VOL_OVERLAY and VPVR document.
    series: 'price' as never,
    calcParams: [DEFAULT_BRACKET_MINUTES, DEFAULT_VALUE_AREA_PCT, DEFAULT_SESSION_ANCHOR],
    /**
     * Figures exist ONLY to feed the price axis — `draw` returns true, so
     * klinecharts never renders them (the PIVOT precedent, pivotPointsIndicator
     * declares figures for exactly this reason).
     *
     * Without them the profile gets clipped. TPO sets `candle.type: 'area'` for
     * its thin close line, and klinecharts' `YAxisImp.calcRange` skips the
     * high/low comparison for an area series — so the axis fits CLOSES while the
     * profile spans HIGH to LOW. Measured on NCRA 5m: axis top 4.48 against a
     * session high of 4.55, clipping the top row.
     *
     * This is the opposite call from VOL_OVERLAY's `figures: []`, and the reason
     * differs: volume magnitudes would destroy a price scale, whereas these
     * values ARE prices and belong in the range.
     */
    figures: [
      { key: 'h', title: '', type: 'line' },
      { key: 'l', title: '', type: 'line' },
    ],
    // NOTE: `shouldOhlc` is deliberately NOT set. In klinecharts' calcRange,
    //   shouldCompareHighLow = (inCandle && !isArea) || (!inCandle && shouldOhlc)
    // so on the candle pane the flag is ignored entirely — it only applies to
    // sub-panes. Since TPO forces `isArea`, the ONLY route to the price axis is
    // the declared figures above. Setting shouldOhlc here would imply otherwise.
    // Behind the close-price line (the VPVR precedent), so the line reads as a
    // path annotation drawn over the profile rather than being buried by it.
    zLevel: -1,
    calc: (dataList: KLineData[], indicator: { calcParams?: unknown[]; extendData?: unknown }) => {
      const ext = indicator.extendData as { tpo?: Partial<TpoSettings> } | undefined
      const tz = sessionTimezoneOf(indicator.extendData)
      // The axis must track the profile's settings (changes re-run calc —
      // overrideIndicator's setExtendData sets klinecharts' calcFlag).
      const settings = normalizeTpoSettings(ext?.tpo)
      const inScope =
        settings.profileSession === 'regular'
          ? (timestamp: number) => isRegularSession(timestamp, tz)
          : undefined
      // Stacked shows the whole day's histogram at any scroll position, so the
      // axis fits the session; expanded shows one column per bracket, so the
      // axis follows only the columns actually on screen.
      if (settings.display === 'expanded') return tpoBarAxisValues(dataList, inScope)
      return tpoAxisValues(dataList, tz, toSessionAnchor(indicator.calcParams?.[2]), inScope)
    },
    draw: ((p: TpoDrawParams & { indicator: Indicator }) => drawTpo(p)) as never,
    createTooltipDataSource: () => ({ name: '', calcParamsText: '', icons: [], values: [] }),
  } as IndicatorTemplateParam)
})

export { TPO_INDICATOR_NAME }
